import { Copy, MoreHorizontal, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ErrorState,
  FeatureErrorBoundary,
  useErrorMessage,
} from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Role } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { RoleDialog } from '../components/role-dialog';
import { useDeleteRole } from '../hooks/use-role-mutations';
import { useRoles } from '../hooks/use-roles';
import { roleErrorTarget } from '../schemas/role-rules';

type Editing = { open: false } | { open: true; role: Role | null; template: Role | null };

function DeleteRoleDialog({
  role,
  onOpenChange,
}: {
  role: Role | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation(['team', 'common']);
  const remove = useDeleteRole();
  const message = useErrorMessage();
  const target = roleErrorTarget(remove.error);
  return (
    <AlertDialog
      open={role !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('team:roles.delete.title', { name: role?.name ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('team:roles.delete.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {target ? t(target.key, target.values) : message(remove.error)}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              event.preventDefault();
              if (role) remove.mutate(role.id, { onSuccess: () => onOpenChange(false) });
            }}
          >
            {remove.isPending ? t('team:roles.delete.submitting') : t('team:roles.delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Roles() {
  const { t } = useTranslation('team');
  const { can, granted } = usePermissions();
  const query = useRoles();
  const [editing, setEditing] = useState<Editing>({ open: false });
  const [deleting, setDeleting] = useState<Role | null>(null);
  const canManage = can('staff.manage');
  const held = [...granted];
  const roles = query.data ?? [];

  const addButton = canManage ? (
    <Button
      data-tour="org.roles.new"
      onClick={() => setEditing({ open: true, role: null, template: null })}
    >
      <Plus aria-hidden />
      {t('roles.create.action')}
    </Button>
  ) : null;

  return (
    <PageContent>
      <PageHeader
        title={t('roles.title')}
        description={t('roles.description')}
        backTo={{ to: '/org/team' }}
        backLabel={t('title')}
        actions={addButton}
      />
      {query.isPending ? (
        <Skeleton className="h-64" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <div className="overflow-hidden rounded-lg border" data-tour="org.roles.table">
          <Table aria-label={t('roles.title')}>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">{t('roles.columns.name')}</TableHead>
                <TableHead>{t('roles.columns.type')}</TableHead>
                <TableHead className="text-right">{t('roles.columns.permissions')}</TableHead>
                <TableHead className="text-right">{t('roles.columns.members')}</TableHead>
                <TableHead className="w-12 pr-4" data-tour="org.roles.row-menu">
                  <span className="sr-only">{t('roles.columns.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id} aria-label={role.name}>
                  <TableCell className="pl-4">
                    <span className="block font-medium">{role.name}</span>
                    <span className="text-muted-foreground font-mono text-[11px]">{role.key}</span>
                  </TableCell>
                  <TableCell>
                    {role.isSystem ? (
                      <Badge variant="secondary">
                        <ShieldCheck aria-hidden />
                        {t('roles.system')}
                      </Badge>
                    ) : (
                      <Badge variant="outline">{t('roles.custom')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {role.permissions.includes('*')
                      ? t('role.allPermissions')
                      : t('role.permissions', { count: role.permissions.length })}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{role.membersCount}</TableCell>
                  <TableCell className="pr-4">
                    {canManage ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t('roles.menu', { name: role.name })}
                          >
                            <MoreHorizontal aria-hidden />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          {role.isSystem ? null : (
                            <DropdownMenuItem
                              onSelect={() => setEditing({ open: true, role, template: null })}
                            >
                              <Pencil aria-hidden />
                              {t('roles.actions.edit')}
                            </DropdownMenuItem>
                          )}
                          {role.permissions.includes('*') ? null : (
                            <DropdownMenuItem
                              onSelect={() =>
                                setEditing({ open: true, role: null, template: role })
                              }
                            >
                              <Copy aria-hidden />
                              {t('roles.actions.copy')}
                            </DropdownMenuItem>
                          )}
                          {role.isSystem ? null : (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                variant="destructive"
                                onSelect={() => setDeleting(role)}
                              >
                                <Trash2 aria-hidden />
                                {t('roles.actions.delete')}
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <p className="text-muted-foreground mt-3 text-sm" data-tour="org.roles.system-hint">
        {t('roles.systemHint')}
      </p>
      {editing.open ? (
        <RoleDialog
          key={editing.role?.id ?? editing.template?.id ?? 'new'}
          role={editing.role}
          template={editing.template}
          held={held}
          open
          onOpenChange={(open) => {
            if (!open) setEditing({ open: false });
          }}
        />
      ) : null}
      <DeleteRoleDialog
        role={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

/** System roles are fixed; custom roles hold any permission the editor holds, except org.write. */
export function RolesPage() {
  return (
    <FeatureErrorBoundary name="team.roles">
      <Roles />
    </FeatureErrorBoundary>
  );
}
